import React, { useEffect, useState } from 'react';
import { Link, useTranslate, useRedirect } from 'react-admin';
import { BarChart, Bar, XAxis, YAxis, Cell, ReferenceLine, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import moment from 'moment';

import { fetchTeamMonthlyReport } from '../../services/api';


const getDeviationColor = (hours, reference, theme) => {
    if (!reference || reference <= 0) {
        return theme.palette.mode === 'dark' ? theme.palette.info.dark : theme.palette.info.light;
    }
    const pct = (hours / reference) * 100;
    if (pct >= 130) return theme.palette.success.main;
    if (pct >= 110) return theme.palette.success.light;
    if (pct >= 85) return theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.info.light;
    if (pct >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
};

const TeamMonthlyProgressCard = () => {
    const [payload, setPayload] = useState();
    const translate = useTranslate();
    const redirect = useRedirect();
    const theme = useTheme();

    useEffect(() => {
        fetchTeamMonthlyReport().then((r) => {
            if (r?.success) setPayload(r.data);
        });
    }, []);

    if (!payload?.users?.length) return null;

    const { users, target, min_required: minRequired, min_acceptable: minAcceptable, median: teamMedian, month_start: monthStart, month_end: monthEnd } = payload;
    const rowHeight = 26;
    const chartHeight = Math.max(users.length * rowHeight + 30, 150);

    const handleBarClick = (data) => {
        if (!data?.id) return;
        const filter = JSON.stringify({
            user_id: data.id,
            group_by: 'project',
            freq: 'daily',
            start_date: monthStart,
            end_date: monthEnd,
        });
        redirect(`/time_entries/stats?filter=${encodeURIComponent(filter)}`);
    };

    return (
        <Card sx={{ height: '100%', padding: '12px' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline" spacing={1}>
                <Link
                    to={`/time_entries/stats?filter={"group_by":"user","freq":"monthly","start_date":"${monthStart}"}`}
                    sx={{ textDecoration: 'none' }}
                >
                    <Typography variant="h6">
                        {translate('Team monthly progress')} ({moment(monthStart).format('MMMM')})
                    </Typography>
                </Link>
                <Stack direction="row" spacing={1.5}>
                    {minAcceptable > 0 && (
                        <Typography variant="caption" sx={{ color: theme.palette.error.light }}>
                            {translate('Min OK')}: {minAcceptable}h
                        </Typography>
                    )}
                    {minRequired > 0 && (
                        <Typography variant="caption" sx={{ color: theme.palette.warning.main }}>
                            {translate('Min')}: {minRequired}h
                        </Typography>
                    )}
                    {teamMedian > 0 && (
                        <Typography variant="caption" sx={{ color: theme.palette.info.main }}>
                            {translate('Median')}: {teamMedian}h
                        </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        {translate('Target')}: {target}h
                    </Typography>
                </Stack>
            </Stack>
            <Box sx={{ width: '100%', height: chartHeight, marginTop: '10px' }}>
                <ResponsiveContainer>
                    <BarChart
                        layout="vertical"
                        data={users}
                        margin={{ top: 5, right: 40, left: 0, bottom: 0 }}
                        barCategoryGap="20%"
                    >
                        <XAxis type="number" domain={[0, target]} hide />
                        <YAxis
                            type="category"
                            dataKey="trigram"
                            width={45}
                            tick={{ fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                        />
                        <Tooltip
                            cursor={{ fill: theme.palette.action.hover }}
                            formatter={(v) => [`${v}h / ${target}h`, translate('Hours')]}
                        />
                        {minAcceptable > 0 && minAcceptable < target && (
                            <ReferenceLine
                                x={minAcceptable}
                                stroke={theme.palette.error.light}
                                strokeDasharray="2 4"
                                ifOverflow="extendDomain"
                            />
                        )}
                        {minRequired > 0 && minRequired < target && (
                            <ReferenceLine
                                x={minRequired}
                                stroke={theme.palette.warning.main}
                                strokeDasharray="3 3"
                                ifOverflow="extendDomain"
                            />
                        )}
                        {teamMedian > 0 && teamMedian < target && (
                            <ReferenceLine
                                x={teamMedian}
                                stroke={theme.palette.info.main}
                                strokeWidth={2}
                                ifOverflow="extendDomain"
                            />
                        )}
                        <ReferenceLine x={target} stroke={theme.palette.divider} strokeDasharray="3 3" />
                        <Bar
                            dataKey="hours"
                            radius={[0, 3, 3, 0]}
                            onClick={handleBarClick}
                            cursor="pointer"
                        >
                            {users.map((u, i) => (
                                <Cell key={i} fill={getDeviationColor(u.hours, teamMedian || minRequired || target, theme)} />
                            ))}
                            <LabelList
                                dataKey="hours"
                                position="right"
                                formatter={(v) => `${v}h`}
                                style={{ fontSize: 11, fill: theme.palette.text.primary }}
                            />
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </Box>
            {teamMedian > 0 && (
                <Stack direction="row" spacing={1.5} sx={{ marginTop: '8px', justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10, fontStyle: 'italic' }}>
                        {translate('vs median')}
                    </Typography>
                    <LegendDot color={theme.palette.error.main} label="< 60%" />
                    <LegendDot color={theme.palette.warning.main} label="60-85%" />
                    <LegendDot color={theme.palette.mode === 'dark' ? theme.palette.info.main : theme.palette.info.light} label="85-110%" />
                    <LegendDot color={theme.palette.success.light} label="110-130%" />
                    <LegendDot color={theme.palette.success.main} label="> 130%" />
                </Stack>
            )}
        </Card>
    );
};

const LegendDot = ({ color, label }) => (
    <Stack direction="row" spacing={0.5} alignItems="center">
        <Box sx={{ width: 10, height: 10, borderRadius: '2px', backgroundColor: color }} />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
    </Stack>
);

export default TeamMonthlyProgressCard;
